import React, { useState, useEffect, useCallback, useMemo, Fragment, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import {
    Box, Typography, Container, Paper, Grid, Button, CircularProgress, Alert,
    Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    MenuItem, Select, FormControl, InputLabel, Tooltip, TablePagination,
    Autocomplete, TextField, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
    ArrowBack, Receipt, TrendingUp, TrendingDown, AutoAwesome, SaveOutlined, CalendarMonth, ExpandMore,
    BusinessCenter, Person, Category, Paid,
} from '@mui/icons-material';
import { PERSONAL_CATEGORIES, PERSONAL_CAT_MAP, PERSONAL_NON_EXPENSE_CODES } from '../constants/personalCategories';
import { TX_CLASSIFICATION_OPTIONS } from '../constants/transactionClassification';
import { D, MONTHS, fmt, fmtSignedNet, selectMenuPropsLarge } from '../constants/plaidDashboardTheme';
import {
    plaidAccountFilterLabel,
    mergePlaidAccountFromList,
    dedupePlaidLinkedAccounts,
} from '../utils/plaidAccountDisplay';

/** TablePagination: fetch every page for the selected month/filters (sequential API calls). */
const ROWS_PER_PAGE_ALL = -1;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500, 1000];
/** Matches OhanaGateway max page size — one request usually loads a full month. */
const ALL_FETCH_BATCH = 50_000;

function initialRowsPerPageFromUrl(searchParams) {
    const r = searchParams.get('rows');
    if (r === 'all') return ROWS_PER_PAGE_ALL;
    const n = parseInt(r || '', 10);
    if (n === ROWS_PER_PAGE_ALL) return ROWS_PER_PAGE_ALL;
    if (PAGE_SIZE_OPTIONS.includes(n)) return n;
    return ROWS_PER_PAGE_ALL;
}

/** Buckets rows under My category (personalCategory); used for deposit/expense tables. */
const UNCATEGORIZED_KEY = '__none__';

function personalCategoryGroupKey(tx) {
    const pc = tx.personalCategory;
    if (pc != null && String(pc).trim() !== '') return String(pc);
    return UNCATEGORIZED_KEY;
}

function labelForPersonalCategoryKey(key) {
    if (key === UNCATEGORIZED_KEY) return 'Uncategorized';
    const c = PERSONAL_CAT_MAP[key];
    return c ? c.label : key;
}

function groupTransactionsByPersonalCategory(transactions) {
    const map = new Map();
    for (const t of transactions) {
        const k = personalCategoryGroupKey(t);
        if (!map.has(k)) map.set(k, []);
        map.get(k).push(t);
    }
    for (const arr of map.values()) {
        arr.sort((a, b) => String(b.transactionDate || '').localeCompare(String(a.transactionDate || '')));
    }
    return [...map.entries()].sort((a, b) => {
        if (a[0] === UNCATEGORIZED_KEY) return -1;
        if (b[0] === UNCATEGORIZED_KEY) return 1;
        return labelForPersonalCategoryKey(a[0]).localeCompare(labelForPersonalCategoryKey(b[0]));
    });
}

function sumTransactionAmounts(rows) {
    return rows.reduce((s, t) => s + parseFloat(t.amount ?? 0), 0);
}

function hasUserClassification(tx) {
    return tx?.classification != null && String(tx.classification).trim() !== '';
}

function hasUserCategory(tx) {
    return tx?.personalCategory != null && String(tx.personalCategory).trim() !== '';
}

function pickPreferredDuplicateTx(current, next) {
    const currentScore = (hasUserClassification(current) ? 2 : 0) + (hasUserCategory(current) ? 2 : 0);
    const nextScore = (hasUserClassification(next) ? 2 : 0) + (hasUserCategory(next) ? 2 : 0);
    if (nextScore !== currentScore) return nextScore > currentScore ? next : current;
    return Number(next?.id || 0) < Number(current?.id || 0) ? next : current;
}

/** Deposits: amounts ≤ 0 — show like row cells (credits as −$). Expenses: positive outflow. Non-expense: signed net. */
function formatCategoryTotalText(sum, kind) {
    if (kind === 'nonexpense') return fmtSignedNet(sum);
    if (kind === 'expense') {
        const v = sum;
        return v > 0 ? `$${fmt(v)}` : `$${fmt(Math.abs(v))}`;
    }
    const v = sum;
    if (v === 0) return `$${fmt(0)}`;
    if (v < 0) return `−$${fmt(Math.abs(v))}`;
    return `$${fmt(v)}`;
}

function isPersonalNonExpenseTx(tx) {
    const pc = tx.personalCategory != null ? String(tx.personalCategory).trim() : '';
    return PERSONAL_NON_EXPENSE_CODES.has(pc);
}

/** Typography tuned for readability when tagging Books / My category */
const TX = {
    cell: '1rem',
    cellBold: '1.02rem',
    head: '0.8125rem',
    amount: '1.05rem',
    chipLabel: '0.75rem',
};

function PlaidAccountTransactions() {
    const [searchParams, setSearchParams] = useSearchParams();

    const [year, setYear] = useState(() => {
        const y = parseInt(searchParams.get('year') || '', 10);
        return y >= 2000 && y <= 2100 ? y : new Date().getFullYear();
    });
    const [txnMonth, setTxnMonth] = useState(() => {
        const m = parseInt(searchParams.get('month') || '', 10);
        return m >= 1 && m <= 12 ? m : new Date().getMonth() + 1;
    });
    const [txnAccount, setTxnAccount] = useState(() => searchParams.get('account') || '');
    const [booksFilter, setBooksFilter] = useState(() => {
        const b = (searchParams.get('books') || 'all').toLowerCase();
        return b === 'business' || b === 'personal' ? b : 'all';
    });

    const [txnRowsPerPage, setTxnRowsPerPage] = useState(() => initialRowsPerPageFromUrl(searchParams));

    useEffect(() => {
        const p = new URLSearchParams();
        p.set('year', String(year));
        p.set('month', String(txnMonth));
        if (txnAccount) p.set('account', txnAccount);
        if (booksFilter === 'business' || booksFilter === 'personal') p.set('books', booksFilter);
        if (txnRowsPerPage === ROWS_PER_PAGE_ALL) p.set('rows', 'all');
        else p.set('rows', String(txnRowsPerPage));
        setSearchParams(p, { replace: true });
    }, [year, txnMonth, txnAccount, booksFilter, txnRowsPerPage, setSearchParams]);

    const [institutions, setInstitutions] = useState([]);
    const [instLoading, setInstLoading] = useState(true);

    const [txns, setTxns] = useState([]);
    const [txnLoading, setTxnLoading] = useState(false);
    const [txnPage, setTxnPage] = useState(0);
    const [txnTotalElements, setTxnTotalElements] = useState(0);

    const [monthBooks, setMonthBooks] = useState(null);
    const [monthBooksLoading, setMonthBooksLoading] = useState(false);
    const [monthBooksSaveLoading, setMonthBooksSaveLoading] = useState(false);

    const [aiCatLoading, setAiCatLoading] = useState(false);
    const [alert, setAlert] = useState(null);

    const txnsFetchAbortRef = useRef(null);

    /** My category groups: default collapsed (totals only); expand to see and edit rows. */
    const [expandedDepositCats, setExpandedDepositCats] = useState(() => new Set());
    const [expandedExpenseCats, setExpandedExpenseCats] = useState(() => new Set());
    const [expandedNonExpenseCats, setExpandedNonExpenseCats] = useState(() => new Set());

    useEffect(() => {
        setExpandedDepositCats(new Set());
        setExpandedExpenseCats(new Set());
        setExpandedNonExpenseCats(new Set());
    }, [year, txnMonth, txnAccount, booksFilter]);

    const loadSummary = useCallback((yr) => {
        api.get('/api/plaid/summary', { params: { year: yr } }).catch(console.error);
    }, []);

    useEffect(() => {
        setInstLoading(true);
        api.get('/api/plaid/institutions')
            .then(res => setInstitutions(res.data || []))
            .catch(console.error)
            .finally(() => setInstLoading(false));
    }, []);

    const allAccountsFlat = useMemo(() => {
        const accts = [];
        institutions.forEach(inst => {
            (inst.accounts || []).forEach(a => accts.push({ ...a, institutionName: inst.name }));
        });
        return accts;
    }, [institutions]);

    /** One menu row per physical account; duplicate Plaid links share {@link linkedAccountIds}. */
    const allAccounts = useMemo(
        () => dedupePlaidLinkedAccounts(allAccountsFlat),
        [allAccountsFlat],
    );

    const accountsSortedForMenu = useMemo(
        () => [...allAccounts].sort((a, b) =>
            plaidAccountFilterLabel(a, allAccounts).localeCompare(
                plaidAccountFilterLabel(b, allAccounts),
                undefined,
                { sensitivity: 'base' },
            ),
        ),
        [allAccounts],
    );

    /** API filter: merged duplicate links use `accountIds`; single id uses `accountId`. */
    const accountQueryParams = useMemo(() => {
        if (!txnAccount) return {};
        const acc = accountsSortedForMenu.find((a) => String(a.id) === String(txnAccount));
        const lids = Array.isArray(acc?.linkedAccountIds) && acc.linkedAccountIds.length > 0
            ? acc.linkedAccountIds
            : [Number(txnAccount)];
        const unique = [...new Set(lids.map(Number).filter((n) => Number.isFinite(n) && n > 0))].sort((a, b) => a - b);
        if (unique.length === 0) return {};
        if (unique.length === 1) return { accountId: unique[0] };
        return { accountIds: unique.join(',') };
    }, [txnAccount, accountsSortedForMenu]);

    useEffect(() => {
        if (!txnAccount || accountsSortedForMenu.length === 0) return;
        const n = Number(txnAccount);
        if (!Number.isFinite(n) || n <= 0) return;
        const row = accountsSortedForMenu.find(
            (a) => Array.isArray(a.linkedAccountIds) && a.linkedAccountIds.includes(n) && Number(a.id) !== n,
        );
        if (row) setTxnAccount(String(row.id));
    }, [txnAccount, accountsSortedForMenu]);

    const accountCanonicalMap = useMemo(() => {
        const out = new Map();
        for (const account of allAccounts) {
            const linked = Array.isArray(account.linkedAccountIds) && account.linkedAccountIds.length > 0
                ? account.linkedAccountIds
                : [account.id];
            const canonical = String(account.id);
            for (const id of linked) {
                out.set(String(id), canonical);
            }
        }
        return out;
    }, [allAccounts]);

    const accountDuplicateGroupMap = useMemo(() => {
        const out = new Map();
        for (const account of allAccounts) {
            const linked = Array.isArray(account.linkedAccountIds) && account.linkedAccountIds.length > 0
                ? account.linkedAccountIds
                : [account.id];
            const hasDuplicateLinks = linked.length > 1;
            for (const id of linked) {
                out.set(String(id), hasDuplicateLinks);
            }
        }
        return out;
    }, [allAccounts]);

    const loadTxns = useCallback(() => {
        txnsFetchAbortRef.current?.abort();
        const controller = new AbortController();
        txnsFetchAbortRef.current = controller;

        setTxnLoading(true);

        const normBooks = (c) => String(c ?? '').trim().toUpperCase();
        const applyBooksClientGuard = (rows, filter) => {
            if (!Array.isArray(rows)) return [];
            if (filter === 'business') return rows.filter((t) => normBooks(t.classification) === 'BUSINESS');
            if (filter === 'personal') return rows.filter((t) => normBooks(t.classification) === 'PERSONAL');
            return rows;
        };

        const applyGuardAndSet = (rawContent, totalElements) => {
            if (controller.signal.aborted) return;
            let content = Array.isArray(rawContent) ? rawContent : [];
            if (booksFilter === 'business' || booksFilter === 'personal') {
                const before = content.length;
                content = applyBooksClientGuard(content, booksFilter);
                if (content.length < before) {
                    console.warn(
                        '[Transactions] Dropped rows that do not match Books filter (stale request or API without books filter). Deploy latest OhanaGateway.',
                        { before, after: content.length, booksFilter },
                    );
                }
            }
            setTxns(content);
            setTxnTotalElements(typeof totalElements === 'number' ? totalElements : content.length);
        };

        const baseParams = {
            year,
            month: txnMonth,
            ...accountQueryParams,
            ...((booksFilter === 'business' || booksFilter === 'personal') ? { books: booksFilter } : {}),
        };

        const run = async () => {
            try {
                if (txnRowsPerPage === ROWS_PER_PAGE_ALL) {
                    const merged = [];
                    let totalEl = 0;
                    for (let pageIdx = 0; ; pageIdx += 1) {
                        if (controller.signal.aborted) return;
                        const { data: d } = await api.get('/api/plaid/transactions', {
                            params: { ...baseParams, page: pageIdx, size: ALL_FETCH_BATCH },
                            signal: controller.signal,
                        });
                        const chunk = Array.isArray(d.content) ? d.content : [];
                        merged.push(...chunk);
                        totalEl = typeof d.totalElements === 'number' ? d.totalElements : merged.length;
                        if (chunk.length === 0) break;
                        if (merged.length >= totalEl) break;
                        if (d.last === true) break;
                        if (chunk.length < ALL_FETCH_BATCH) break;
                    }
                    if (controller.signal.aborted) return;
                    applyGuardAndSet(merged, totalEl);
                } else {
                    const { data: d } = await api.get('/api/plaid/transactions', {
                        params: { ...baseParams, page: txnPage, size: txnRowsPerPage },
                        signal: controller.signal,
                    });
                    if (controller.signal.aborted) return;
                    const chunk = Array.isArray(d?.content) ? d.content : [];
                    const totalEl = typeof d.totalElements === 'number' ? d.totalElements : chunk.length;
                    applyGuardAndSet(chunk, totalEl);
                }
            } catch (err) {
                if (controller.signal.aborted || err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;
                console.error(err);
            } finally {
                if (!controller.signal.aborted) setTxnLoading(false);
            }
        };

        void run();
    }, [year, txnMonth, txnPage, txnRowsPerPage, booksFilter, accountQueryParams]);

    useEffect(() => { loadTxns(); }, [loadTxns]);

    useEffect(() => () => {
        txnsFetchAbortRef.current?.abort();
    }, []);

    const loadMonthBooks = useCallback((opts) => {
        const silent = opts && opts.silent;
        if (!silent) setMonthBooksLoading(true);
        const params = { year, month: txnMonth, ...accountQueryParams };
        api.get('/api/plaid/month-books', { params })
            .then(res => setMonthBooks(res.data))
            .catch(() => setMonthBooks(null))
            .finally(() => { if (!silent) setMonthBooksLoading(false); });
    }, [year, txnMonth, accountQueryParams]);

    useEffect(() => { loadMonthBooks(); }, [loadMonthBooks]);

    const monthBooksNet = useMemo(() => {
        if (!monthBooks) return null;
        const be = parseFloat(monthBooks.businessExpense ?? 0);
        const bi = parseFloat(monthBooks.businessIncome ?? 0);
        const pe = parseFloat(monthBooks.personalExpense ?? 0);
        const pi = parseFloat(monthBooks.personalIncome ?? 0);
        return {
            businessNet: bi - be,
            personalNet: pi - pe,
            combinedNet: (bi - be) + (pi - pe),
        };
    }, [monthBooks]);

    const handleCategoryChange = useCallback(async (id, code) => {
        const normalized = code === '' || code == null ? null : code;
        const matches = (t) => t.id === id || Number(t.id) === Number(id);
        setTxns(prev => prev.map(t => (matches(t) ? { ...t, personalCategory: normalized } : t)));
        try {
            await api.put(`/api/plaid/transactions/${id}/category`, { personalCategory: normalized });
            loadSummary(year);
            loadMonthBooks({ silent: true });
        } catch (err) {
            console.error('Failed to update category:', err);
            loadTxns();
        }
    }, [year, loadSummary, loadTxns, loadMonthBooks]);

    const handleClassificationChange = useCallback(async (id, value) => {
        const next = value === '' || value == null ? null : value;
        const matches = (t) => t.id === id || Number(t.id) === Number(id);
        setTxns(prev => prev.map(t => (matches(t) ? { ...t, classification: next } : t)));
        try {
            await api.put(`/api/plaid/transactions/${id}/category`, {
                classification: value || '',
            });
            loadSummary(year);
            loadMonthBooks({ silent: true });
            if (booksFilter === 'business' || booksFilter === 'personal') {
                loadTxns();
            }
        } catch (err) {
            console.error('Failed to update classification:', err);
            loadTxns();
        }
    }, [year, loadSummary, loadTxns, loadMonthBooks, booksFilter]);

    const handleAiCategorizePage = useCallback(async () => {
        const targets = txns.filter(t => t.personalCategory == null || String(t.personalCategory).trim() === '');
        if (targets.length === 0) {
            setAlert({ severity: 'info', message: 'No uncategorized transactions on this page.' });
            return;
        }
        setAiCatLoading(true);
        setAlert(null);
        try {
            const { data } = await api.post('/api/plaid/auto-categorize', targets.map(t => ({ id: t.id })));
            const n = data?.categorized ?? 0;
            const rules = data?.ruleBasedCount ?? 0;
            const ruleNote = rules > 0 ? ` (${rules} by merchant rules: Safeway → Personal / Groceries, Walmart → Business / Office supplies)` : '';
            setAlert({ severity: 'success', message: `Set Books + My category on ${n} of ${targets.length} uncategorized transaction(s) on this page.${ruleNote}` });
            loadTxns();
            loadSummary(year);
            loadMonthBooks({ silent: true });
        } catch (err) {
            setAlert({ severity: 'error', message: `AI categorize failed: ${err.response?.data?.error || err.message}` });
        } finally {
            setAiCatLoading(false);
        }
    }, [txns, loadTxns, loadSummary, year, loadMonthBooks]);

    const handleSaveMonthBooks = useCallback(async () => {
        setMonthBooksSaveLoading(true);
        setAlert(null);
        try {
            const { data } = await api.post('/api/plaid/month-books/save', {
                year,
                month: txnMonth,
                accountId: 0,
            });
            setMonthBooks(data);
            setAlert({ severity: 'success', message: data.message || 'Month books saved.' });
        } catch (err) {
            setAlert({ severity: 'error', message: err.response?.data?.error || err.message });
        } finally {
            setMonthBooksSaveLoading(false);
        }
    }, [year, txnMonth]);

    const selectFieldSx = {
        '& .MuiInputBase-root': {
            backgroundColor: D.card,
            color: D.fg,
            borderRadius: 2,
            fontSize: '1rem',
            minHeight: 48,
        },
        '& .MuiOutlinedInput-root': {
            backgroundColor: D.card,
            color: D.fg,
        },
        '& .MuiSelect-select': {
            color: `${D.fg} !important`,
            py: 1.25,
        },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: D.border },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: D.muted },
        '& .MuiInputLabel-root': { color: D.muted, fontSize: '0.95rem' },
        '& .MuiInputLabel-root.Mui-focused': { color: D.primary },
        '& .MuiSvgIcon-root': { color: D.muted },
    };

    const selectInTableLarge = {
        color: D.fg,
        fontSize: TX.cell,
        '& .MuiInputBase-root': {
            backgroundColor: D.card,
            color: D.fg,
            minHeight: 48,
        },
        '& .MuiOutlinedInput-root': { backgroundColor: D.card, color: D.fg, minHeight: 48 },
        '& .MuiSelect-select': { color: `${D.fg} !important`, fontSize: '0.98rem', py: 1.1, display: 'flex', alignItems: 'center' },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: D.border },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: D.muted },
        '& .MuiSvgIcon-root': { color: D.muted, fontSize: 22 },
    };

    /**
     * Plaid: positive amount = expense (debit); zero or negative = deposit/credit (money in).
     * My category "Non-Expense" (e.g. h51 Account Transfer, h52 Credit Card Payment) has its own section
     * so positive transfer debits are not mixed into true expenses.
     */
    const displayTxns = useMemo(() => {
        const bySignature = new Map();
        const out = [];
        for (const tx of txns) {
            const accountId = tx.account?.id != null ? String(tx.account.id) : '';
            const canonicalAccountId = accountCanonicalMap.get(accountId);
            if (!canonicalAccountId || !accountDuplicateGroupMap.get(accountId)) {
                out.push(tx);
                continue;
            }

            const signature = [
                canonicalAccountId,
                String(tx.transactionDate || ''),
                String(tx.amount ?? ''),
                String(tx.description || '').trim().toLowerCase(),
                String(tx.merchantName || '').trim().toLowerCase(),
            ].join('|');
            const existing = bySignature.get(signature);
            if (!existing) {
                bySignature.set(signature, { index: out.length, tx });
                out.push(tx);
                continue;
            }

            if (String(existing.tx.account?.id || '') === accountId) {
                out.push(tx);
                continue;
            }

            const preferred = pickPreferredDuplicateTx(existing.tx, tx);
            if (preferred !== existing.tx) {
                out[existing.index] = preferred;
                bySignature.set(signature, { index: existing.index, tx: preferred });
            }
        }
        return out;
    }, [txns, accountCanonicalMap, accountDuplicateGroupMap]);

    const nonExpenseTxns = useMemo(
        () => displayTxns.filter((t) => isPersonalNonExpenseTx(t)),
        [displayTxns],
    );
    const depositTxns = useMemo(
        () => displayTxns.filter((t) => parseFloat(t.amount) <= 0 && !isPersonalNonExpenseTx(t)),
        [displayTxns],
    );
    const expenseTxns = useMemo(
        () => displayTxns.filter((t) => parseFloat(t.amount) > 0 && !isPersonalNonExpenseTx(t)),
        [displayTxns],
    );

    const depositGrouped = useMemo(
        () => groupTransactionsByPersonalCategory(depositTxns),
        [depositTxns],
    );
    const expenseGrouped = useMemo(
        () => groupTransactionsByPersonalCategory(expenseTxns),
        [expenseTxns],
    );
    const nonExpenseGrouped = useMemo(
        () => groupTransactionsByPersonalCategory(nonExpenseTxns),
        [nonExpenseTxns],
    );

    const classificationStats = useMemo(() => {
        const norm = (v) => String(v ?? '').trim().toUpperCase();
        const business = displayTxns.filter((t) => norm(t.classification) === 'BUSINESS').length;
        const personal = displayTxns.filter((t) => norm(t.classification) === 'PERSONAL').length;
        const categorized = displayTxns.filter((t) => t.personalCategory != null && String(t.personalCategory).trim() !== '').length;
        return {
            total: displayTxns.length,
            business,
            personal,
            unclassified: Math.max(displayTxns.length - business - personal, 0),
            categorized,
            uncategorized: Math.max(displayTxns.length - categorized, 0),
            categorizedPct: displayTxns.length > 0 ? Math.round((categorized / displayTxns.length) * 100) : 0,
        };
    }, [displayTxns]);

    const visibleBooks = useMemo(() => {
        const norm = (v) => String(v ?? '').trim().toUpperCase();
        const totals = {
            businessExpense: 0,
            businessIncome: 0,
            personalExpense: 0,
            personalIncome: 0,
        };
        for (const tx of displayTxns) {
            if (isPersonalNonExpenseTx(tx)) continue;
            const classification = norm(tx.classification);
            const amount = parseFloat(tx.amount ?? 0);
            if (classification === 'BUSINESS') {
                if (amount > 0) totals.businessExpense += amount;
                else totals.businessIncome += Math.abs(amount);
            } else if (classification === 'PERSONAL') {
                if (amount > 0) totals.personalExpense += amount;
                else totals.personalIncome += Math.abs(amount);
            }
        }
        return {
            ...totals,
            businessNet: totals.businessIncome - totals.businessExpense,
            personalNet: totals.personalIncome - totals.personalExpense,
        };
    }, [displayTxns]);

    const duplicateHiddenCount = Math.max(txns.length - displayTxns.length, 0);

    /** Uncategorized + full list; extras = codes on screen but missing from PERSONAL_CATEGORIES */
    const myCategoryOptions = useMemo(() => {
        const none = { code: '', label: 'Uncategorized', group: '—' };
        const nonExpenseFirst = PERSONAL_CATEGORIES.filter((c) => c.group === 'Non-Expense').map((c) => ({
            code: c.code,
            label: c.label,
            group: c.group,
        }));
        const restPersonal = PERSONAL_CATEGORIES.filter((c) => c.group !== 'Non-Expense').map((c) => ({
            code: c.code,
            label: c.label,
            group: c.group,
        }));
        /* Non-Expense first so transfers / card payments are easy to find; Uncategorized next. */
        const base = [...nonExpenseFirst, none, ...restPersonal];
        const known = new Set(base.map((o) => o.code));
        const extras = [];
        for (const t of displayTxns) {
            const c = t.personalCategory != null ? String(t.personalCategory).trim() : '';
            if (c && !known.has(c)) {
                known.add(c);
                extras.push({
                    code: c,
                    label: PERSONAL_CAT_MAP[c]?.label ?? c,
                    group: 'Other',
                });
            }
        }
        extras.sort((a, b) => a.label.localeCompare(b.label));
        return [...base, ...extras];
    }, [displayTxns]);

    const filterMyCategoryOptions = useCallback((options, { inputValue }) => {
        const q = inputValue.trim().toLowerCase();
        if (!q) return options;
        return options.filter(
            (o) =>
                o.label.toLowerCase().includes(q)
                || o.code.toLowerCase().includes(q)
                || (o.group && o.group.toLowerCase().includes(q)),
        );
    }, []);

    const resolveMyCategoryOption = useCallback(
        (tx) => {
            const code = tx.personalCategory != null ? String(tx.personalCategory).trim() : '';
            return myCategoryOptions.find((o) => o.code === code)
                ?? myCategoryOptions.find((o) => o.code === '');
        },
        [myCategoryOptions],
    );

    const TxTableHead = () => (
        <TableHead>
            <TableRow>
                {['Date', 'Merchant', 'Description', 'Account', 'Amount', 'Bank category', 'Books', 'My category'].map((h, idx) => (
                    <TableCell key={h} align={idx === 4 ? 'right' : 'left'}
                        sx={{
                            bgcolor: D.secondary,
                            fontWeight: 700,
                            fontSize: TX.head,
                            color: D.muted,
                            borderBottom: `1px solid ${D.border}`,
                            ...((h === 'My category') ? { minWidth: 280 } : {}),
                            ...((h === 'Books') ? { minWidth: 160 } : {}),
                            ...((h === 'Description') ? { minWidth: 220 } : {}),
                        }}>
                        {h}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );

    const renderTxnRow = (tx) => (
        <TableRow key={tx.id} hover sx={{ '&:hover': { bgcolor: D.secondary }, '& td': { borderColor: D.border } }}>
            <TableCell sx={{ fontSize: TX.cell, whiteSpace: 'nowrap', color: D.fg }}>{tx.transactionDate}</TableCell>
            <TableCell sx={{ fontSize: TX.cellBold, fontWeight: 600, maxWidth: 220, color: D.fg }}>
                {tx.merchantName || '—'}
            </TableCell>
            <TableCell sx={{ fontSize: TX.cell, maxWidth: 280 }}>
                <Tooltip title={tx.description || ''} arrow slotProps={{
                    tooltip: { sx: { bgcolor: D.card, color: D.fg, border: `1px solid ${D.border}`, fontSize: '0.95rem', maxWidth: 400 } },
                }}>
                    <span style={{ color: D.fg }}>{tx.description || '—'}</span>
                </Tooltip>
            </TableCell>
            <TableCell sx={{ fontSize: '0.95rem', color: D.muted }}>
                {plaidAccountFilterLabel(mergePlaidAccountFromList(tx.account, allAccounts), allAccounts)}
            </TableCell>
            <TableCell align="right" sx={{
                fontWeight: 700,
                fontSize: TX.amount,
                color: tx.amount > 0 ? D.spend : D.success,
            }}>
                {tx.amount > 0 ? '' : '−'}${fmt(Math.abs(tx.amount))}
            </TableCell>
            <TableCell>
                {tx.plaidCategory && (
                    <Chip label={tx.plaidCategory} sx={{
                        height: 30,
                        fontSize: TX.chipLabel,
                        bgcolor: `${D.primary}22`,
                        color: D.primary,
                        border: 'none',
                        fontWeight: 600,
                    }} />
                )}
            </TableCell>
            <TableCell>
                <Select
                    displayEmpty
                    fullWidth
                    value={tx.classification || ''}
                    onChange={e => handleClassificationChange(tx.id, e.target.value)}
                    MenuProps={selectMenuPropsLarge}
                    sx={{ ...selectInTableLarge, minWidth: 150 }}
                    renderValue={(v) => {
                        const opt = TX_CLASSIFICATION_OPTIONS.find(o => o.value === v);
                        return opt ? opt.label : <em style={{ color: D.muted, fontStyle: 'normal' }}>Unclassified</em>;
                    }}
                >
                    {TX_CLASSIFICATION_OPTIONS.map(o => (
                        <MenuItem key={o.value || 'none'} value={o.value} sx={{ fontSize: '0.95rem' }}>
                            {o.value ? o.label : <em>Unclassified</em>}
                        </MenuItem>
                    ))}
                </Select>
            </TableCell>
            <TableCell sx={{ minWidth: 280 }}>
                <Autocomplete
                    size="small"
                    options={myCategoryOptions}
                    groupBy={(option) => option.group}
                    value={resolveMyCategoryOption(tx)}
                    onChange={(_, newValue) => handleCategoryChange(tx.id, newValue?.code ?? null)}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(a, b) => a.code === b.code}
                    filterOptions={filterMyCategoryOptions}
                    autoHighlight
                    selectOnFocus
                    handleHomeEndKeys
                    clearOnEscape
                    blurOnSelect
                    noOptionsText="No categories match"
                    sx={{
                        minWidth: 260,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: D.card,
                            color: D.fg,
                            minHeight: 46,
                            fontSize: '0.98rem',
                            py: 0.25,
                        },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: D.border },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: D.muted },
                        '& .MuiAutocomplete-input': {
                            color: `${D.fg} !important`,
                            textOverflow: 'ellipsis',
                        },
                        '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: D.muted, fontSize: 22 },
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                bgcolor: D.card,
                                border: `1px solid ${D.border}`,
                                mt: 0.5,
                            },
                        },
                        listbox: {
                            sx: {
                                maxHeight: 360,
                                bgcolor: D.card,
                                '& .MuiAutocomplete-groupLabel': {
                                    bgcolor: D.secondary,
                                    color: D.primary,
                                    fontWeight: 700,
                                    fontSize: '0.82rem',
                                    lineHeight: 2.2,
                                    px: 1.5,
                                    borderBottom: `1px solid ${D.border}`,
                                },
                                '& .MuiAutocomplete-option': {
                                    fontSize: '0.95rem',
                                    minHeight: 44,
                                    color: D.fg,
                                },
                            },
                        },
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Search category…"
                            variant="outlined"
                            InputProps={{
                                ...params.InputProps,
                                sx: { pr: 1 },
                            }}
                        />
                    )}
                />
            </TableCell>
        </TableRow>
    );

    const moneyMetricCards = monthBooks ? [
        {
            key: 'business-income',
            label: 'Business revenue',
            value: `$${fmt(visibleBooks.businessIncome)}`,
            sub: 'Money in tagged Business',
            icon: TrendingUp,
            color: D.income,
            bg: 'rgba(94,234,212,0.10)',
        },
        {
            key: 'business-expense',
            label: 'Business expense',
            value: `$${fmt(visibleBooks.businessExpense)}`,
            sub: 'Deductible or operating spend',
            icon: BusinessCenter,
            color: D.spend,
            bg: 'rgba(251,113,133,0.10)',
        },
        {
            key: 'business-net',
            label: 'Business net',
            value: fmtSignedNet(visibleBooks.businessNet),
            sub: 'Revenue minus expense',
            icon: Paid,
            color: visibleBooks.businessNet >= 0 ? D.success : D.spend,
            bg: `${D.primary}12`,
            featured: true,
        },
        {
            key: 'personal-income',
            label: 'Personal revenue',
            value: `$${fmt(visibleBooks.personalIncome)}`,
            sub: 'Money in tagged Personal',
            icon: TrendingUp,
            color: D.income,
            bg: 'rgba(94,234,212,0.10)',
        },
        {
            key: 'personal-expense',
            label: 'Personal expense',
            value: `$${fmt(visibleBooks.personalExpense)}`,
            sub: 'Household and personal spend',
            icon: Person,
            color: D.spend,
            bg: 'rgba(251,113,133,0.10)',
        },
        {
            key: 'personal-net',
            label: 'Personal net',
            value: fmtSignedNet(visibleBooks.personalNet),
            sub: 'Income minus expense',
            icon: Paid,
            color: visibleBooks.personalNet >= 0 ? D.success : D.spend,
            bg: `${D.primary}12`,
            featured: true,
        },
    ] : [];

    if (instLoading) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: D.bg }}>
                <CircularProgress size={44} sx={{ color: D.primary }} />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: D.bg, color: D.fg }}>
            <Box sx={{ borderBottom: `1px solid ${D.border}`, bgcolor: D.cardHeaderBg }}>
                <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                    <Button component={Link} to="/linked-accounts" startIcon={<ArrowBack />} sx={{
                        color: D.muted, textTransform: 'none', mb: 1.5,
                        '&:hover': { color: D.fg, bgcolor: D.secondary },
                    }}>
                        Back to Accounts overview
                    </Button>
                    <Box display="flex" flexWrap="wrap" alignItems="center" gap={2} justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <Box sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 3,
                                display: 'grid',
                                placeItems: 'center',
                                bgcolor: `${D.primary}22`,
                                border: `1px solid ${D.primary}55`,
                            }}>
                                <Receipt sx={{ fontSize: 28, color: D.primary }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: { xs: '1.35rem', sm: '1.6rem' }, fontWeight: 800, color: D.fg, letterSpacing: '-0.02em' }}>
                                    Transaction workspace
                                </Typography>
                                <Typography sx={{ fontSize: '0.95rem', color: D.muted }}>
                                    Tag Books as Business or Personal, categorize each row, and watch revenue and expenses update.
                                </Typography>
                            </Box>
                        </Box>
                        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
                            <FormControl size="medium" sx={{ minWidth: 120, ...selectFieldSx }}>
                                <InputLabel id="tx-year-lbl">Year</InputLabel>
                                <Select
                                    labelId="tx-year-lbl"
                                    label="Year"
                                    value={year}
                                    onChange={e => { setYear(Number(e.target.value)); setTxnPage(0); }}
                                    MenuProps={selectMenuPropsLarge}
                                    sx={{ borderRadius: 2 }}
                                >
                                    {[2024, 2025, 2026, 2027].map(y => (
                                        <MenuItem key={y} value={y} sx={{ fontSize: '1rem' }}>{y}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Box display="flex" alignItems="center" gap={0.75} sx={{ color: D.muted }}>
                                <CalendarMonth sx={{ fontSize: 22 }} />
                            </Box>
                            <FormControl size="medium" sx={{ minWidth: 150, ...selectFieldSx }}>
                                <InputLabel id="tx-month-lbl">Month</InputLabel>
                                <Select
                                    labelId="tx-month-lbl"
                                    label="Month"
                                    value={String(txnMonth)}
                                    onChange={(e) => {
                                        setTxnPage(0);
                                        setTxnMonth(Number(e.target.value));
                                    }}
                                    MenuProps={selectMenuPropsLarge}
                                    sx={{ borderRadius: 2 }}
                                >
                                    {MONTHS.map((label, i) => (
                                        <MenuItem key={label} value={String(i + 1)} sx={{ fontSize: '1rem' }}>{label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="medium" sx={{ minWidth: 200, ...selectFieldSx }}>
                                <InputLabel>Account</InputLabel>
                                <Select
                                    value={txnAccount}
                                    label="Account"
                                    onChange={(e) => { setTxnPage(0); setTxnAccount(e.target.value); }}
                                    MenuProps={selectMenuPropsLarge}
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="" sx={{ fontSize: '1rem' }}>All accounts</MenuItem>
                                    {accountsSortedForMenu.map(a => (
                                        <MenuItem key={a.id} value={String(a.id)} sx={{ fontSize: '1rem' }}>
                                            {plaidAccountFilterLabel(a, allAccounts)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Box sx={{
                                display: 'inline-flex',
                                flexDirection: 'column',
                                gap: 0.75,
                                flexBasis: { xs: '100%', md: 'auto' },
                                width: { xs: '100%', md: 'auto' },
                                mt: { xs: 0.5, md: 0 },
                            }}>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: D.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                    Books
                                </Typography>
                                <ToggleButtonGroup
                                    exclusive
                                    value={booksFilter}
                                    onChange={(_, v) => {
                                        if (v != null) {
                                            setTxnPage(0);
                                            setBooksFilter(v);
                                        }
                                    }}
                                    size="small"
                                    sx={{
                                        '& .MuiToggleButton-root': {
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.9rem',
                                            color: D.muted,
                                            borderColor: D.border,
                                            px: 1.75,
                                            py: 0.75,
                                            '&.Mui-selected': {
                                                color: D.primaryFg,
                                                bgcolor: D.primary,
                                                borderColor: D.primary,
                                                '&:hover': { bgcolor: D.primary },
                                            },
                                        },
                                    }}
                                >
                                    <ToggleButton value="all">All</ToggleButton>
                                    <ToggleButton value="business">Business</ToggleButton>
                                    <ToggleButton value="personal">Personal</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
                {alert && (
                    <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 2, borderRadius: 2 }}>
                        {alert.message}
                    </Alert>
                )}

                <Paper elevation={0} sx={{
                    mb: 2.5,
                    p: { xs: 2, md: 3 },
                    borderRadius: 4,
                    overflow: 'hidden',
                    position: 'relative',
                    border: `1px solid ${D.border}`,
                    background: `radial-gradient(circle at top right, ${D.primary}22 0, transparent 32%), linear-gradient(135deg, ${D.card} 0%, #171821 100%)`,
                }}>
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        background: 'linear-gradient(90deg, rgba(255,255,255,0.04), transparent 40%)',
                    }} />
                    <Box position="relative" display="flex" flexDirection={{ xs: 'column', lg: 'row' }} gap={2.5} justifyContent="space-between">
                        <Box flex={1}>
                            <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={1.5}>
                                <Chip label={`${MONTHS[txnMonth - 1]} ${year}`} size="small"
                                    sx={{ bgcolor: `${D.primary}24`, color: D.primary, fontWeight: 800, height: 28 }} />
                                <Chip label={booksFilter === 'all' ? 'All Books' : booksFilter === 'business' ? 'Business view' : 'Personal view'} size="small"
                                    sx={{ bgcolor: D.secondary, color: D.fg, fontWeight: 700, height: 28, border: `1px solid ${D.border}` }} />
                                <Chip label={`${classificationStats.total.toLocaleString()} loaded`} size="small"
                                    sx={{ bgcolor: D.secondary, color: D.muted, fontWeight: 700, height: 28, border: `1px solid ${D.border}` }} />
                                {duplicateHiddenCount > 0 && (
                                    <Chip label={`${duplicateHiddenCount} duplicate link row${duplicateHiddenCount === 1 ? '' : 's'} hidden`} size="small"
                                        sx={{ bgcolor: `${D.primary}22`, color: D.primary, fontWeight: 800, height: 28, border: `1px solid ${D.primary}44` }} />
                                )}
                            </Box>
                            <Typography sx={{ fontSize: { xs: '1.35rem', md: '1.8rem' }, fontWeight: 850, color: D.fg, letterSpacing: '-0.03em', lineHeight: 1.15 }}>
                                Separate business from personal, then clean up categories.
                            </Typography>
                            <Typography sx={{ mt: 1, fontSize: '0.95rem', color: D.muted, maxWidth: 760 }}>
                                Use the Books column for tax/business separation and My category for your own expense buckets. The cards below summarize revenue, expenses, and categorization progress for the selected period.
                            </Typography>
                        </Box>
                        <Box sx={{
                            minWidth: { xs: '100%', lg: 320 },
                            p: 2,
                            borderRadius: 3,
                            bgcolor: 'rgba(0,0,0,0.18)',
                            border: `1px solid ${D.border}`,
                        }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Category sx={{ color: D.primary, fontSize: 20 }} />
                                    <Typography sx={{ color: D.fg, fontWeight: 800 }}>Categorization</Typography>
                                </Box>
                                <Typography sx={{ color: D.primary, fontWeight: 900, fontSize: '1.2rem' }}>
                                    {classificationStats.categorizedPct}%
                                </Typography>
                            </Box>
                            <Box sx={{ height: 8, borderRadius: 999, bgcolor: D.secondary, overflow: 'hidden', mb: 1.25 }}>
                                <Box sx={{ height: '100%', width: `${classificationStats.categorizedPct}%`, bgcolor: D.primary, borderRadius: 999 }} />
                            </Box>
                            <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={1}>
                                {[
                                    ['Business', classificationStats.business, D.income],
                                    ['Personal', classificationStats.personal, D.primary],
                                    ['Unclassified', classificationStats.unclassified, D.spend],
                                    ['Uncategorized', classificationStats.uncategorized, D.muted],
                                ].map(([label, value, color]) => (
                                    <Box key={label} sx={{ p: 1, borderRadius: 2, bgcolor: D.secondary }}>
                                        <Typography sx={{ color: D.muted, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}>{label}</Typography>
                                        <Typography sx={{ color, fontSize: '1.05rem', fontWeight: 850 }}>{Number(value).toLocaleString()}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Paper>

                {monthBooks && (
                    <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                        {moneyMetricCards.map(({ key, label, value, sub, icon: Icon, color, bg, featured }) => (
                            <Grid item xs={12} sm={6} lg={4} xl={2} key={key}>
                                <Paper elevation={0} sx={{
                                    height: '100%',
                                    p: 2,
                                    borderRadius: 3,
                                    bgcolor: D.card,
                                    border: featured ? `1px solid ${D.primary}66` : `1px solid ${D.border}`,
                                    boxShadow: featured ? `0 0 0 1px ${D.primary}1a inset` : 'none',
                                }}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1.25}>
                                        <Box sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: 2,
                                            display: 'grid',
                                            placeItems: 'center',
                                            bgcolor: bg,
                                            border: `1px solid ${color}33`,
                                        }}>
                                            <Icon sx={{ color, fontSize: 20 }} />
                                        </Box>
                                        {featured && <Chip label="Net" size="small" sx={{ height: 22, bgcolor: `${D.primary}22`, color: D.primary, fontWeight: 800 }} />}
                                    </Box>
                                    <Typography sx={{ color: D.muted, fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        {label}
                                    </Typography>
                                    <Typography sx={{ color, fontSize: '1.45rem', fontWeight: 850, mt: 0.35, letterSpacing: '-0.03em' }}>
                                        {value}
                                    </Typography>
                                    <Typography sx={{ color: D.muted, fontSize: '0.78rem', mt: 0.5 }}>
                                        {sub}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                )}

                <Paper elevation={0} sx={{
                    mb: 2, p: 2.5, borderRadius: 2,
                    border: `1px solid ${D.border}`,
                    bgcolor: D.card,
                }}>
                    <Box display="flex" flexWrap="wrap" alignItems="flex-start" justifyContent="space-between" gap={2} mb={1.5}>
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: D.fg }}>
                                Monthly books — {MONTHS[txnMonth - 1]} {year}
                            </Typography>
                            <Typography sx={{ fontSize: '0.85rem', color: D.muted, mt: 0.5, maxWidth: 720 }}>
                                Four totals include every linked account. Business / Personal nets = earnings − expenses for that Books bucket; combined = both. Table below can filter to one account.
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            disabled={monthBooksLoading || monthBooksSaveLoading || duplicateHiddenCount > 0}
                            onClick={handleSaveMonthBooks}
                            startIcon={monthBooksSaveLoading ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />}
                            sx={{ textTransform: 'none', bgcolor: D.primary, color: D.primaryFg, px: 2, py: 1 }}
                        >
                            {monthBooksSaveLoading ? 'Saving…' : duplicateHiddenCount > 0 ? 'Save disabled until duplicates are cleaned' : 'Save month to database'}
                        </Button>
                    </Box>
                    {monthBooksLoading && !monthBooks ? (
                        <Box display="flex" justifyContent="center" py={3}>
                            <CircularProgress sx={{ color: D.primary }} />
                        </Box>
                    ) : monthBooks ? (
                        <>
                            <Grid container spacing={1.5}>
                                {[
                                    { key: 'be', label: 'Business expense', val: visibleBooks.businessExpense, color: D.spend, icon: TrendingDown },
                                    { key: 'bi', label: 'Business earning', val: visibleBooks.businessIncome, color: D.income, icon: TrendingUp },
                                    { key: 'pe', label: 'Personal expense', val: visibleBooks.personalExpense, color: D.spend, icon: TrendingDown },
                                    { key: 'pi', label: 'Personal income', val: visibleBooks.personalIncome, color: D.income, icon: TrendingUp },
                                ].map(({ key, label, val, color, icon: Icon }) => (
                                    <Grid item xs={6} md={3} key={key}>
                                        <Box sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${D.border}`, bgcolor: D.secondary }}>
                                            <Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
                                                <Icon sx={{ fontSize: 18, color }} />
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: D.muted, textTransform: 'uppercase' }}>
                                                    {label}
                                                </Typography>
                                            </Box>
                                            <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: D.fg }}>
                                                ${fmt(val)}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                            {monthBooksNet && (
                                <Grid container spacing={1.5} sx={{ mt: 1.5 }}>
                                    {[
                                        { key: 'bn', label: 'Business net', sub: 'Earnings − expenses', val: visibleBooks.businessNet, emphasize: false },
                                        { key: 'pn', label: 'Personal net', sub: 'Earnings − expenses', val: visibleBooks.personalNet, emphasize: false },
                                        { key: 'cn', label: 'Combined net', sub: 'Business + personal', val: visibleBooks.businessNet + visibleBooks.personalNet, emphasize: true },
                                    ].map(({ key, label, sub, val, emphasize }) => {
                                        const positive = val >= 0;
                                        return (
                                            <Grid item xs={12} md={4} key={key}>
                                                <Box sx={{
                                                    p: 1.5, borderRadius: 2,
                                                    border: emphasize ? `2px solid ${D.primary}66` : `1px solid ${D.border}`,
                                                    bgcolor: emphasize ? `${D.primary}12` : D.secondary,
                                                }}>
                                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: D.muted, textTransform: 'uppercase' }}>
                                                        {label}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.72rem', color: D.muted }}>{sub}</Typography>
                                                    <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: positive ? D.success : D.spend, mt: 0.5 }}>
                                                        {fmtSignedNet(val)}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            )}
                            <Box display="flex" flexWrap="wrap" gap={1} mt={1.5}>
                                {(classificationStats.unclassified > 0) && (
                                    <Chip
                                        label={`${classificationStats.unclassified} unclassified in view`}
                                        sx={{ bgcolor: `${D.primary}22`, color: D.primary, fontSize: '0.8rem', height: 28 }}
                                    />
                                )}
                                {duplicateHiddenCount > 0 && (
                                    <Chip
                                        label={`${duplicateHiddenCount} duplicate linked transaction${duplicateHiddenCount === 1 ? '' : 's'} hidden from totals`}
                                        sx={{ bgcolor: `${D.primary}22`, color: D.primary, fontSize: '0.8rem', height: 28 }}
                                    />
                                )}
                                {monthBooks.savedSnapshot?.savedAt && (
                                    <Typography sx={{ fontSize: '0.8rem', color: D.muted }}>
                                        Last saved: {new Date(monthBooks.savedSnapshot.savedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                                    </Typography>
                                )}
                            </Box>
                        </>
                    ) : null}
                </Paper>

                <Box display="flex" flexWrap="wrap" alignItems="center" gap={2} mb={2}>
                    <Button
                        variant="outlined"
                        disabled={txnLoading || aiCatLoading || displayTxns.length === 0}
                        onClick={handleAiCategorizePage}
                        startIcon={aiCatLoading ? <CircularProgress size={18} sx={{ color: D.primary }} /> : <AutoAwesome />}
                        sx={{
                            borderColor: D.border,
                            color: D.primary,
                            textTransform: 'none',
                            fontSize: '1rem',
                            py: 1,
                            px: 2,
                            '&:hover': { borderColor: D.primary, bgcolor: `${D.primary}14` },
                        }}
                    >
                        {aiCatLoading ? 'AI…' : 'AI My category (this page)'}
                    </Button>
                    <Typography sx={{ fontSize: '1rem', color: D.muted }}>
                        {txnLoading ? 'Loading…' : `${txnTotalElements.toLocaleString()} transaction${txnTotalElements === 1 ? '' : 's'}`}
                    </Typography>
                    {(booksFilter === 'business' || booksFilter === 'personal') && (
                        <Chip
                            label={`Books: ${booksFilter === 'business' ? 'Business' : 'Personal'} only`}
                            size="small"
                            sx={{
                                height: 28,
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                bgcolor: `${D.primary}22`,
                                color: D.primary,
                                border: 'none',
                            }}
                        />
                    )}
                </Box>

                {txnLoading ? (
                    <Box display="flex" justifyContent="center" py={6}>
                        <CircularProgress size={40} sx={{ color: D.primary }} />
                    </Box>
                ) : (
                    <>
                        {displayTxns.length === 0 ? (
                            <Paper elevation={0} sx={{ p: 5, borderRadius: 2, border: `1px solid ${D.border}`, bgcolor: D.card, textAlign: 'center' }}>
                                <Typography sx={{ color: D.muted, fontSize: '1.1rem' }}>
                                    {(booksFilter === 'business' || booksFilter === 'personal')
                                        ? `No transactions for this period with Books set to ${booksFilter === 'business' ? 'Business' : 'Personal'}. Try "All" or tag rows in Books.`
                                        : 'No transactions for this period'}
                                </Typography>
                            </Paper>
                        ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${D.border}`, overflow: 'hidden', bgcolor: D.card }}>
                                <Box sx={{
                                    px: 2, py: 1.5,
                                    bgcolor: `${D.success}12`,
                                    borderBottom: `1px solid ${D.border}`,
                                }}>
                                    <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: D.success }}>
                                        Deposits & credits (money in)
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.88rem', color: D.muted, mt: 0.5 }}>
                                        Categories show combined totals; expand a category to see and re-tag transactions. This uses a single page scroll (no inner panel scrollers). Non-Expense tags (account transfers, credit card payments) are listed below, not here. {depositTxns.length} row{depositTxns.length === 1 ? '' : 's'} loaded.
                                    </Typography>
                                    {depositGrouped.length > 0 && (
                                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                            <Button
                                                type="button"
                                                variant="text"
                                                size="small"
                                                onClick={() => setExpandedDepositCats(new Set(depositGrouped.map(([k]) => k)))}
                                                sx={{ color: D.primary, fontSize: '0.85rem', textTransform: 'none', minWidth: 0, px: 0.5 }}
                                            >
                                                Expand all categories
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="text"
                                                size="small"
                                                onClick={() => setExpandedDepositCats(new Set())}
                                                sx={{ color: D.primary, fontSize: '0.85rem', textTransform: 'none', minWidth: 0, px: 0.5 }}
                                            >
                                                Collapse all
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                                <TableContainer sx={{
                                    overflow: 'visible',
                                    '& .MuiTableCell-root': { py: 1.5, px: 1.75 },
                                }}>
                                    <Table size="medium">
                                        <TxTableHead />
                                        <TableBody>
                                            {depositGrouped.map(([catKey, rows]) => {
                                                const isOpen = expandedDepositCats.has(catKey);
                                                const total = sumTransactionAmounts(rows);
                                                return (
                                                <Fragment key={`dep-${catKey}`}>
                                                    <TableRow
                                                        onClick={() => setExpandedDepositCats((prev) => {
                                                            const next = new Set(prev);
                                                            if (next.has(catKey)) next.delete(catKey);
                                                            else next.add(catKey);
                                                            return next;
                                                        })}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                setExpandedDepositCats((prev) => {
                                                                    const next = new Set(prev);
                                                                    if (next.has(catKey)) next.delete(catKey);
                                                                    else next.add(catKey);
                                                                    return next;
                                                                });
                                                            }
                                                        }}
                                                        tabIndex={0}
                                                        aria-expanded={isOpen}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            '& td': { borderColor: D.border },
                                                            '&:hover td': { bgcolor: `${D.success}18` },
                                                        }}
                                                    >
                                                        <TableCell colSpan={4} sx={{
                                                            py: 1.25,
                                                            px: 2,
                                                            bgcolor: `${D.success}0d`,
                                                            borderBottom: `1px solid ${D.border}`,
                                                        }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                <ExpandMore sx={{
                                                                    color: D.muted,
                                                                    fontSize: 22,
                                                                    transform: isOpen ? 'rotate(180deg)' : 'none',
                                                                    transition: 'transform 0.2s',
                                                                }} />
                                                                <Typography component="span" sx={{ fontWeight: 800, fontSize: '0.95rem', color: D.fg }}>
                                                                    {labelForPersonalCategoryKey(catKey)}
                                                                </Typography>
                                                                <Typography component="span" sx={{ ml: 0.5, fontSize: '0.85rem', color: D.muted }}>
                                                                    {rows.length} txn{rows.length === 1 ? '' : 's'}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell align="right" sx={{
                                                            py: 1.25,
                                                            px: 2,
                                                            bgcolor: `${D.success}0d`,
                                                            borderBottom: `1px solid ${D.border}`,
                                                            fontWeight: 800,
                                                            fontSize: TX.amount,
                                                            color: total < 0 ? D.success : D.fg,
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                            {formatCategoryTotalText(total, 'deposit')}
                                                        </TableCell>
                                                        <TableCell colSpan={3} sx={{
                                                            py: 1.25,
                                                            px: 2,
                                                            bgcolor: `${D.success}0d`,
                                                            borderBottom: `1px solid ${D.border}`,
                                                        }}>
                                                            <Typography sx={{ fontSize: '0.82rem', color: D.muted, fontStyle: isOpen ? 'normal' : 'italic' }}>
                                                                {isOpen ? 'Click header to collapse' : 'Click to expand transactions'}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                    {isOpen && rows.map(tx => renderTxnRow(tx))}
                                                </Fragment>
                                            );})}
                                            {depositTxns.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={8} align="center" sx={{ py: 3, color: D.muted, fontSize: '1rem' }}>
                                                        No deposits / credits on this page
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>

                            <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${D.border}`, overflow: 'hidden', bgcolor: D.card }}>
                                <Box sx={{
                                    px: 2, py: 1.5,
                                    bgcolor: `${D.spend}10`,
                                    borderBottom: `1px solid ${D.border}`,
                                }}>
                                    <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: D.spend }}>
                                        Expenses (money out)
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.88rem', color: D.muted, mt: 0.5 }}>
                                        Categories show combined totals; expand a category to see and re-tag transactions. This uses a single page scroll (no inner panel scrollers). Non-Expense tags (account transfers, credit card payments) are listed below, not here. {expenseTxns.length} row{expenseTxns.length === 1 ? '' : 's'} loaded.
                                    </Typography>
                                    {expenseGrouped.length > 0 && (
                                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                            <Button
                                                type="button"
                                                variant="text"
                                                size="small"
                                                onClick={() => setExpandedExpenseCats(new Set(expenseGrouped.map(([k]) => k)))}
                                                sx={{ color: D.primary, fontSize: '0.85rem', textTransform: 'none', minWidth: 0, px: 0.5 }}
                                            >
                                                Expand all categories
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="text"
                                                size="small"
                                                onClick={() => setExpandedExpenseCats(new Set())}
                                                sx={{ color: D.primary, fontSize: '0.85rem', textTransform: 'none', minWidth: 0, px: 0.5 }}
                                            >
                                                Collapse all
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                                <TableContainer sx={{
                                    overflow: 'visible',
                                    '& .MuiTableCell-root': { py: 1.5, px: 1.75 },
                                }}>
                                    <Table size="medium">
                                        <TxTableHead />
                                        <TableBody>
                                            {expenseGrouped.map(([catKey, rows]) => {
                                                const isOpen = expandedExpenseCats.has(catKey);
                                                const total = sumTransactionAmounts(rows);
                                                return (
                                                <Fragment key={`exp-${catKey}`}>
                                                    <TableRow
                                                        onClick={() => setExpandedExpenseCats((prev) => {
                                                            const next = new Set(prev);
                                                            if (next.has(catKey)) next.delete(catKey);
                                                            else next.add(catKey);
                                                            return next;
                                                        })}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                setExpandedExpenseCats((prev) => {
                                                                    const next = new Set(prev);
                                                                    if (next.has(catKey)) next.delete(catKey);
                                                                    else next.add(catKey);
                                                                    return next;
                                                                });
                                                            }
                                                        }}
                                                        tabIndex={0}
                                                        aria-expanded={isOpen}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            '& td': { borderColor: D.border },
                                                            '&:hover td': { bgcolor: `${D.spend}18` },
                                                        }}
                                                    >
                                                        <TableCell colSpan={4} sx={{
                                                            py: 1.25,
                                                            px: 2,
                                                            bgcolor: `${D.spend}0d`,
                                                            borderBottom: `1px solid ${D.border}`,
                                                        }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                <ExpandMore sx={{
                                                                    color: D.muted,
                                                                    fontSize: 22,
                                                                    transform: isOpen ? 'rotate(180deg)' : 'none',
                                                                    transition: 'transform 0.2s',
                                                                }} />
                                                                <Typography component="span" sx={{ fontWeight: 800, fontSize: '0.95rem', color: D.fg }}>
                                                                    {labelForPersonalCategoryKey(catKey)}
                                                                </Typography>
                                                                <Typography component="span" sx={{ ml: 0.5, fontSize: '0.85rem', color: D.muted }}>
                                                                    {rows.length} txn{rows.length === 1 ? '' : 's'}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell align="right" sx={{
                                                            py: 1.25,
                                                            px: 2,
                                                            bgcolor: `${D.spend}0d`,
                                                            borderBottom: `1px solid ${D.border}`,
                                                            fontWeight: 800,
                                                            fontSize: TX.amount,
                                                            color: total > 0 ? D.spend : D.muted,
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                            {formatCategoryTotalText(total, 'expense')}
                                                        </TableCell>
                                                        <TableCell colSpan={3} sx={{
                                                            py: 1.25,
                                                            px: 2,
                                                            bgcolor: `${D.spend}0d`,
                                                            borderBottom: `1px solid ${D.border}`,
                                                        }}>
                                                            <Typography sx={{ fontSize: '0.82rem', color: D.muted, fontStyle: isOpen ? 'normal' : 'italic' }}>
                                                                {isOpen ? 'Click header to collapse' : 'Click to expand transactions'}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                    {isOpen && rows.map(tx => renderTxnRow(tx))}
                                                </Fragment>
                                            );})}
                                            {expenseTxns.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={8} align="center" sx={{ py: 3, color: D.muted, fontSize: '1rem' }}>
                                                        No expenses on this page
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>

                            <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${D.border}`, overflow: 'hidden', bgcolor: D.card }}>
                                <Box sx={{
                                    px: 2, py: 1.5,
                                    bgcolor: `${D.primary}12`,
                                    borderBottom: `1px solid ${D.border}`,
                                }}>
                                    <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: D.primary }}>
                                        Non-Expense (transfers & card payments)
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.88rem', color: D.muted, mt: 0.5 }}>
                                        My category <strong>Account Transfer (Non-Expense)</strong> or <strong>Credit Card Payment</strong> — cash moving between accounts, not ordinary spend or income. Grouped here regardless of Plaid amount sign, with the same single-page scrolling UX. {nonExpenseTxns.length} row{nonExpenseTxns.length === 1 ? '' : 's'} loaded.
                                    </Typography>
                                    {nonExpenseGrouped.length > 0 && (
                                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                            <Button
                                                type="button"
                                                variant="text"
                                                size="small"
                                                onClick={() => setExpandedNonExpenseCats(new Set(nonExpenseGrouped.map(([k]) => k)))}
                                                sx={{ color: D.primary, fontSize: '0.85rem', textTransform: 'none', minWidth: 0, px: 0.5 }}
                                            >
                                                Expand all categories
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="text"
                                                size="small"
                                                onClick={() => setExpandedNonExpenseCats(new Set())}
                                                sx={{ color: D.primary, fontSize: '0.85rem', textTransform: 'none', minWidth: 0, px: 0.5 }}
                                            >
                                                Collapse all
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                                <TableContainer sx={{
                                    overflow: 'visible',
                                    '& .MuiTableCell-root': { py: 1.5, px: 1.75 },
                                }}>
                                    <Table size="medium">
                                        <TxTableHead />
                                        <TableBody>
                                            {nonExpenseGrouped.map(([catKey, rows]) => {
                                                const isOpen = expandedNonExpenseCats.has(catKey);
                                                const total = sumTransactionAmounts(rows);
                                                return (
                                                <Fragment key={`ne-${catKey}`}>
                                                    <TableRow
                                                        onClick={() => setExpandedNonExpenseCats((prev) => {
                                                            const next = new Set(prev);
                                                            if (next.has(catKey)) next.delete(catKey);
                                                            else next.add(catKey);
                                                            return next;
                                                        })}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                setExpandedNonExpenseCats((prev) => {
                                                                    const next = new Set(prev);
                                                                    if (next.has(catKey)) next.delete(catKey);
                                                                    else next.add(catKey);
                                                                    return next;
                                                                });
                                                            }
                                                        }}
                                                        tabIndex={0}
                                                        aria-expanded={isOpen}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            '& td': { borderColor: D.border },
                                                            '&:hover td': { bgcolor: `${D.primary}18` },
                                                        }}
                                                    >
                                                        <TableCell colSpan={4} sx={{
                                                            py: 1.25,
                                                            px: 2,
                                                            bgcolor: `${D.primary}0d`,
                                                            borderBottom: `1px solid ${D.border}`,
                                                        }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                <ExpandMore sx={{
                                                                    color: D.muted,
                                                                    fontSize: 22,
                                                                    transform: isOpen ? 'rotate(180deg)' : 'none',
                                                                    transition: 'transform 0.2s',
                                                                }} />
                                                                <Typography component="span" sx={{ fontWeight: 800, fontSize: '0.95rem', color: D.fg }}>
                                                                    {labelForPersonalCategoryKey(catKey)}
                                                                </Typography>
                                                                <Typography component="span" sx={{ ml: 0.5, fontSize: '0.85rem', color: D.muted }}>
                                                                    {rows.length} txn{rows.length === 1 ? '' : 's'}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell align="right" sx={{
                                                            py: 1.25,
                                                            px: 2,
                                                            bgcolor: `${D.primary}0d`,
                                                            borderBottom: `1px solid ${D.border}`,
                                                            fontWeight: 800,
                                                            fontSize: TX.amount,
                                                            color: D.fg,
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                            {formatCategoryTotalText(total, 'nonexpense')}
                                                        </TableCell>
                                                        <TableCell colSpan={3} sx={{
                                                            py: 1.25,
                                                            px: 2,
                                                            bgcolor: `${D.primary}0d`,
                                                            borderBottom: `1px solid ${D.border}`,
                                                        }}>
                                                            <Typography sx={{ fontSize: '0.82rem', color: D.muted, fontStyle: isOpen ? 'normal' : 'italic' }}>
                                                                {isOpen ? 'Click header to collapse' : 'Click to expand transactions'}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                    {isOpen && rows.map(tx => renderTxnRow(tx))}
                                                </Fragment>
                                            );})}
                                            {nonExpenseTxns.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={8} align="center" sx={{ py: 3, color: D.muted, fontSize: '1rem' }}>
                                                        No transactions tagged Non-Expense for this period
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Box>
                        )}
                        {txnRowsPerPage === ROWS_PER_PAGE_ALL ? (
                            <Paper elevation={0} sx={{
                                mt: 1,
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${D.border}`,
                                bgcolor: D.card,
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                            }}>
                                <Typography sx={{ fontSize: '1rem', color: D.muted }}>
                                    Showing all <strong style={{ color: D.fg }}>{txnTotalElements.toLocaleString()}</strong> transaction
                                    {txnTotalElements === 1 ? '' : 's'} for this filter ({displayTxns.length.toLocaleString()} shown after duplicate-link cleanup).
                                </Typography>
                                <Button
                                    variant="outlined"
                                    onClick={() => { setTxnRowsPerPage(50); setTxnPage(0); }}
                                    sx={{ textTransform: 'none', borderColor: D.border, color: D.primary }}
                                >
                                    Use pagination (50 / page)
                                </Button>
                            </Paper>
                        ) : (
                            <TablePagination
                                component="div"
                                count={txnTotalElements}
                                page={txnPage}
                                onPageChange={(_, p) => setTxnPage(p)}
                                rowsPerPage={txnRowsPerPage}
                                onRowsPerPageChange={(e) => {
                                    const v = parseInt(e.target.value, 10);
                                    setTxnRowsPerPage(Number.isNaN(v) ? 50 : v);
                                    setTxnPage(0);
                                }}
                                rowsPerPageOptions={[...PAGE_SIZE_OPTIONS, { value: ROWS_PER_PAGE_ALL, label: 'All (this period)' }]}
                                sx={{
                                    color: D.muted,
                                    borderTop: `1px solid ${D.border}`,
                                    bgcolor: D.card,
                                    mt: 1,
                                    borderRadius: 2,
                                    '& .MuiTablePagination-toolbar': { minHeight: 56 },
                                    '& .MuiTablePagination-select': { fontSize: '1rem', color: D.fg },
                                    '& .MuiInputBase-root': { fontSize: '1rem' },
                                    '& .MuiTablePagination-displayedRows': { fontSize: '1rem' },
                                    '& .MuiIconButton-root': { color: D.muted },
                                }}
                            />
                        )}
                    </>
                )}
            </Container>
        </Box>
    );
}

export default PlaidAccountTransactions;
